import { useState } from 'react'

import { Controller, FormProvider } from 'react-hook-form'

import { PlanName } from '@tribeplatform/gql-client/types'
import { Form } from '@tribeplatform/react-sdk/components'
import { useAuthToken } from '@tribeplatform/react-sdk/hooks'
import { Alert } from '@tribeplatform/react-ui-kit/Alert'
import { Button } from '@tribeplatform/react-ui-kit/Button'
import { FormControl } from '@tribeplatform/react-ui-kit/FormControl'

import { useMemberCapacity } from '../../hooks/useMemberCapacity'
import { CustomMessageInput } from './CustomMessageInput'
import { EmailNameArrayInput } from './EmailNameArrayInput'
import { RoleSelect } from './RoleSelect'
import { SpaceMultipleAutocomplete } from './SpaceMultipleAutocomplete'
import { useUserImportContext } from './UserImportContext'
import { UserImportFormProps, useUserImportForm } from './useUserImportForm'
import { isAdminEmailNotConfirmed } from './utils'

export interface FormProps {
  onCancel: () => void
  onSubmit: (result: UserImportFormProps) => void
  loading: boolean
}

export const UserImportForm: React.FC<FormProps> = ({
  onSubmit,
  onCancel,
  loading,
}) => {
  const context = useUserImportContext()
  const { data: authToken } = useAuthToken()
  const [customMessageExceedsLimit, setCustomMessageExceedsLimit] =
    useState(false)
  const {
    didReachLimit,
    isApproachingLimit,
    isLoading: isMemberCapacityLoading,
    memberCapacity,
    memberCapacityDeclared,
    totalInvitationCount,
  } = useMemberCapacity()
  const {
    data: { network },
  } = useAuthToken()
  const plan = network?.subscriptionPlan
  const trial = plan?.trial

  const shouldDisplayMembersCapacity =
    trial ||
    plan?.name === PlanName.BASIC ||
    (plan?.name === PlanName.PLUS && isApproachingLimit) ||
    (plan?.name === PlanName.PLUS && didReachLimit) ||
    (plan?.name === PlanName.PREMIUM && isApproachingLimit) ||
    (plan?.name === PlanName.PREMIUM && didReachLimit) ||
    (plan?.name === PlanName.ENTERPRISE && isApproachingLimit) ||
    (plan?.name === PlanName.ENTERPRISE && didReachLimit)

  const formMethods = useUserImportForm()

  const {
    form: methods,
    hideManyAtOnce,
    manyAtOnce,
    parseMultipleEmails,
  } = formMethods || {}

  const submitDisabled =
    isAdminEmailNotConfirmed(authToken.member) || customMessageExceedsLimit

  const {
    control,
    formState: { errors },
    handleSubmit,
    register,
    setValue,
  } = formMethods.form || {}

  const submit = data => {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { multipleEmails, ...rest } = data

    // After deleting last entry it's not being reflected in
    // the form's state so we get rid of empty internal entries
    const entries = rest.entries.filter(({ email }) => email)

    onSubmit({ ...rest, entries })
  }

  const invitationsLeft =
    memberCapacity - (memberCapacityDeclared + totalInvitationCount)
  // const planName = enumI18nPlanName(plan?.name)?.toLowerCase()
  const planName = plan?.name

  return (
    <FormProvider {...{ ...methods, ...formMethods }}>
      <form onSubmit={handleSubmit(submit)}>
        <div className="flex flex-col space-y-5">
          {!manyAtOnce ? (
            <>
              <EmailNameArrayInput name="entries" />

              {context && (
                <Controller
                  control={control}
                  name="spaces"
                  defaultValue={context?.defaultSpaces}
                  render={({
                    field: { onChange, value },
                    fieldState: { error },
                  }) => (
                    <FormControl>
                      <FormControl.Label>Spaces to join</FormControl.Label>
                      <SpaceMultipleAutocomplete
                        value={value}
                        onChange={onChange}
                      />
                      {error?.message && (
                        <FormControl.HelperText invalid className="mt-2">
                          {error?.message}
                        </FormControl.HelperText>
                      )}
                    </FormControl>
                  )}
                  rules={{
                    required: "This field can't be empty",
                    validate: {
                      minlength: value =>
                        Array.isArray(value) && value.length > 0,
                    },
                  }}
                />
              )}

              {false && context && (
                <Controller
                  control={control}
                  name="role"
                  defaultValue={context?.defaultRole}
                  render={({
                    field: { onChange, onBlur, value, name },
                    fieldState: { error },
                  }) => (
                    <FormControl>
                      <FormControl.Label>Role</FormControl.Label>
                      <RoleSelect
                        name={name}
                        options={context.roles}
                        onChange={onChange}
                        onBlur={onBlur}
                        value={value}
                      />
                      {error?.message && (
                        <FormControl.HelperText invalid className="mt-2">
                          {error?.message}
                        </FormControl.HelperText>
                      )}
                    </FormControl>
                  )}
                  rules={{
                    required: "This field can't be empty",
                  }}
                />
              )}

              <CustomMessageInput
                setExceedsLimit={setCustomMessageExceedsLimit}
                register={register}
                errors={errors}
                setValue={setValue}
              />

              <div className="flex flex-col space-y-5">
                <div>
                  {/*        {isMemberCapacityLoading && (
                    <Skeleton width={72} height={8} />
                  )} */}

                  {!isMemberCapacityLoading && shouldDisplayMembersCapacity && (
                    <Alert
                      status={didReachLimit ? 'error' : 'warning'}
                      title=""
                    >
                      <strong>{invitationsLeft} invitations</strong> left out of{' '}
                      {memberCapacity} for your {planName}{' '}
                      {trial ? 'trial ' : ''}plan
                      {/*          <UpgradeTouchpointLink>
                          <Trans
                            i18nKey="member:upgradeToSendMore"
                            defaults="Upgrade to send more"
                          />
                        </UpgradeTouchpointLink> */}
                    </Alert>
                  )}
                </div>

                <Form.Actions>
                  <Button
                    type="submit"
                    variant="primary"
                    disabled={submitDisabled}
                    data-testid="submit-import-form"
                    loading={loading}
                  >
                    Submit
                  </Button>
                  <Button
                    variant="outline"
                    onClick={onCancel}
                    data-testid="cancel-import-form"
                  >
                    Cancel
                  </Button>
                </Form.Actions>
              </div>
            </>
          ) : (
            <div className="flex flex-col space-y-5">
              <FormControl.Textarea
                label="Enter multiple email addresses"
                helperText="Use commas to separate addresses"
                {...register('multipleEmails')}
                data-testid="invite-modal-multiple-emails-textarea"
                error={errors?.multipleEmails?.message}
              />
              <Form.Actions>
                <Button
                  variant="primary"
                  data-testid="invite-modal-add-invitees-button"
                  onClick={parseMultipleEmails}
                >
                  Add invitees
                </Button>
                <Button type="reset" variant="outline" onClick={hideManyAtOnce}>
                  Back
                </Button>
              </Form.Actions>
            </div>
          )}
        </div>
      </form>
    </FormProvider>
  )
}
